import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, orderBy, isEmpty } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import {
  InventoryIncentivesModel,
  getLoanSelectedIdsPath,
  getLeaseSelectedIdsPath,
  InventoryIncentivesEntities,
} from 'client/data/models/inventory-incentives';
import { Incentive } from 'site-modules/shared/constants/car-buying/models';
/* Utils */
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import {
  getIncompatibleIncentivesIds,
  filterLeaseIncentivesByTerm,
} from 'site-modules/shared/utils/incentives/incentives-service';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getIsNonTMVDealer } from 'site-modules/shared/utils/inventory/vdp-utils';
/* Constants */
import { INCENTIVE_VALUES } from 'site-modules/shared/constants/inventory/price-analysis';
import { TrackingConstant } from 'client/tracking/constant';
/* Components */
import { withIncentives } from 'site-modules/shared/components/inventory/with-incentives/with-incentives';
import { PriceCheckerIncentivesList } from 'site-modules/shared/components/price-checker/price-checker-incentives-list/price-checker-incentives-list';

import './price-checker-incentives.scss';

const DISABLE_INCENTIVES_MAKES = ['Acura', 'INFINITI'];
const DEFAULT_ITEMS_NUMBER = 2;
export const INCOMPATIBILITY_MSG =
  'One or more of the following incentives cannot be combined with the current selections';

export function PriceCheckerIncentivesUI({
  vinData,
  isLeaseView = false,
  setModelValue,
  primaryLoanIncentives = [],
  primaryLeaseIncentives = [],
  conditionalLoanIncentives = [],
  conditionalLeaseIncentives = [],
  selectedLeaseIncentives = [],
  selectedLoanIncentives = [],
  selectedLeaseTerm = null,
}) {
  const [isExpanded, setIsExpanded] = useToggle(false);

  const selectedIncentivesIds = (isLeaseView ? selectedLeaseIncentives : selectedLoanIncentives).map(({ id }) => id);

  const primaryIncentives = isLeaseView
    ? filterLeaseIncentivesByTerm(primaryLeaseIncentives, selectedLeaseTerm)
    : primaryLoanIncentives;
  const conditionalIncentives = isLeaseView
    ? filterLeaseIncentivesByTerm(conditionalLeaseIncentives, selectedLeaseTerm)
    : conditionalLoanIncentives;
  const sortedIncentives = [
    ...orderBy(primaryIncentives, INCENTIVE_VALUES.REBATE, 'desc'),
    ...orderBy(conditionalIncentives, [INCENTIVE_VALUES.REBATE, 'type', 'subtype'], ['desc', 'asc', 'asc']),
  ];

  const hideIncentives =
    isNew(get(vinData, 'type', '')) &&
    DISABLE_INCENTIVES_MAKES.includes(get(vinData, 'vehicleInfo.styleInfo.make', ''));

  const visibleRebates = sortedIncentives.slice(0, DEFAULT_ITEMS_NUMBER);
  const collapsedRebates = sortedIncentives.slice(DEFAULT_ITEMS_NUMBER);

  const incompatibleIncentivesIds = getIncompatibleIncentivesIds({
    selectedIncentives: isLeaseView ? selectedLeaseIncentives : selectedLoanIncentives,
    allIncentives: [...primaryIncentives, ...conditionalIncentives],
  });

  const fireIncentiveSelection = useCallback((id, selected) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        action_name: TrackingConstant.ACTION_PURCHASE_VEHICLE,
        subaction_name: selected ? TrackingConstant.SELECT_INCENTIVES : TrackingConstant.REMOVE_INCENTIVES,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: 'edm-entry-price-analysis',
        value: id,
      },
    });
  }, []);

  const updateSelectedIncentives = useCallback(
    ({ incentive, checked }) => {
      const incentives = isLeaseView ? selectedLeaseIncentives : selectedLoanIncentives;
      const newSelectedIncentives = checked
        ? [...incentives, incentive]
        : incentives?.filter(currentIncentive => currentIncentive.id !== incentive.id);

      setModelValue(
        isLeaseView ? getLeaseSelectedIdsPath(vinData.vin) : getLoanSelectedIdsPath(vinData.vin),
        InventoryIncentivesModel,
        newSelectedIncentives.map(({ id }) => id)
      );
    },
    [selectedLeaseIncentives, selectedLoanIncentives, setModelValue, isLeaseView, vinData]
  );

  const toggleIncentiveSelection = useCallback(
    incentive => {
      const { id } = incentive;
      const checked = !selectedIncentivesIds?.includes(id);
      fireIncentiveSelection(id, checked);

      updateSelectedIncentives({ incentive, checked });
    },
    [fireIncentiveSelection, selectedIncentivesIds, updateSelectedIncentives]
  );
  if (hideIncentives) return null;

  const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const isNonTMVDealer = getIsNonTMVDealer(franchiseId);

  return (
    <div className="price-checker-incentives mt-1_5">
      <div>
        <h3 className="fw-bold mb-0_75 size-16">Which incentives apply to you?</h3>
        {!isEmpty(incompatibleIncentivesIds) && (
          <div className="incompatibility-msg d-inline-block text-italic small text-cool-gray-30 p-0_5 mb-0_75 rounded-12 bg-cool-gray-90">
            {INCOMPATIBILITY_MSG}
          </div>
        )}
        {sortedIncentives.length ? (
          <div>
            <div className="pos-r">
              <PriceCheckerIncentivesList
                selectedIncentives={isLeaseView ? selectedLeaseIncentives : selectedLoanIncentives}
                incentives={visibleRebates}
                onClick={toggleIncentiveSelection}
                incompatibleIncentivesIds={incompatibleIncentivesIds}
                isLeaseView={isLeaseView}
                isNonTMVDealer={isNonTMVDealer}
                selectedLeaseTerm={selectedLeaseTerm}
              />
              {!!collapsedRebates?.length && !isExpanded && <div className="gradient pos-a w-100" />}
            </div>
            {!!collapsedRebates?.length && (
              <>
                {isExpanded && !!sortedIncentives.length && (
                  <PriceCheckerIncentivesList
                    isFirstItemInitiallyFocused
                    selectedIncentives={isLeaseView ? selectedLeaseIncentives : selectedLoanIncentives}
                    incentives={collapsedRebates}
                    onClick={toggleIncentiveSelection}
                    incompatibleIncentivesIds={incompatibleIncentivesIds}
                    isLeaseView={isLeaseView}
                    isNonTMVDealer={isNonTMVDealer}
                    selectedLeaseTerm={selectedLeaseTerm}
                  />
                )}
                <div className="mb-md-0_5 d-flex align-items-center justify-content-start">
                  <Button
                    size="sm"
                    color="link"
                    className="p-0 size-16 text-primary-darker more-info-btn"
                    onClick={setIsExpanded}
                    data-tracking-id={isExpanded ? null : 'view_content_summary'}
                    data-tracking-value="show-incentives"
                    aria-expanded={isExpanded}
                    data-no-refresh
                  >
                    <span className="me-0_5 fw-normal text-transform-none">
                      {isExpanded ? 'Hide all incentives' : 'Show all incentives'}
                    </span>
                    <span
                      aria-hidden
                      className={classNames('size-14', isExpanded ? 'icon-arrow-up4' : 'icon-arrow-down4')}
                    />
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-cool-gray-50">None currently available</div>
        )}
      </div>
    </div>
  );
}

PriceCheckerIncentivesUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  vinData: InventoryEntities.InventoryVin.isRequired,
  isLeaseView: PropTypes.bool,
  selectedLoanIncentives: PropTypes.arrayOf(InventoryIncentivesEntities.Incentive),
  selectedLeaseIncentives: PropTypes.arrayOf(Incentive),
  primaryLeaseIncentives: PropTypes.arrayOf(Incentive),
  primaryLoanIncentives: PropTypes.arrayOf(InventoryIncentivesEntities.Incentive),
  conditionalLoanIncentives: PropTypes.arrayOf(InventoryIncentivesEntities.Incentive),
  conditionalLeaseIncentives: PropTypes.arrayOf(Incentive),
  selectedLeaseTerm: PropTypes.number,
};

export const PriceCheckerIncentives = withIncentives(PriceCheckerIncentivesUI);
